import React, { useState } from "react";
import { toast } from "sonner";
import { UploadCloud, X, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { resolveImageUrl } from "../lib/format";

const CONDITIONS = ["Excellent", "Good", "Average", "Needs Attention"];

const SellYourCar = () => {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", location: "",
    brand: "", model: "", variant: "", manufacturing_year: "", registration_year: "",
    registration_number: "", kilometres: "", fuel: "Petrol", transmission: "Automatic",
    ownership: "1st", colour: "", condition: "Good", expected_price: "",
  });
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    const fd = new FormData();
    Array.from(files).slice(0, 10).forEach((f) => fd.append("files", f));
    try {
      const r = await api.post("/public/uploads/sell-car", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImages((prev) => [...prev, ...r.data.files]);
    } catch (e) {
      toast.error("Upload failed");
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.brand || !form.model) {
      toast.error("Please fill required fields"); return;
    }
    setBusy(true);
    try {
      await api.post("/sell-car", {
        ...form,
        manufacturing_year: Number(form.manufacturing_year) || new Date().getFullYear(),
        registration_year: form.registration_year ? Number(form.registration_year) : null,
        kilometres: Number(form.kilometres) || 0,
        expected_price: form.expected_price ? Number(form.expected_price) : null,
        image_urls: images.map((i) => i.url),
      });
      setDone(true);
    } catch (err) { toast.error("Something went wrong"); }
    finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="container-wide py-24 text-center max-w-xl mx-auto" data-testid="sell-success">
        <div className="overline text-[#7A1519]">Enquiry Received</div>
        <h1 className="font-display text-3xl md:text-4xl mt-3 font-semibold">Thank you — we've got your details.</h1>
        <p className="mt-4 text-[#242424]/75">Our advisor will review the details and reach out with the next steps.</p>
      </div>
    );
  }

  const fieldProps = (name) => ({ value: form[name], onChange: (v) => setForm((prev) => ({ ...prev, [name]: v })) });

  return (
    <div className="container-wide py-16 max-w-4xl mx-auto" data-testid="page-sell-car">
      <div className="overline text-[#7A1519]">Sell Your Car</div>
      <h1 className="font-display text-4xl md:text-6xl mt-3 font-semibold tracking-tighter">Get a fair valuation.</h1>
      <p className="mt-4 text-[#242424]/75 max-w-2xl">Share details and photos of your vehicle. We'll get in touch with a transparent, no-pressure offer.</p>

      <form onSubmit={submit} className="mt-10 bg-white border hairline p-6 md:p-10 rounded-sm space-y-8">
        <Section title="About you">
          <Field {...fieldProps("name")} label="Full Name *" testId="sc-name" />
          <Field {...fieldProps("phone")} label="Phone *" testId="sc-phone" />
          <Field {...fieldProps("email")} label="Email *" type="email" testId="sc-email" />
          <Field {...fieldProps("location")} label="City / Location" testId="sc-location" />
        </Section>

        <Section title="About your car">
          <Field {...fieldProps("brand")} label="Brand *" testId="sc-brand" />
          <Field {...fieldProps("model")} label="Model *" testId="sc-model" />
          <Field {...fieldProps("variant")} label="Variant" testId="sc-variant" />
          <Field {...fieldProps("manufacturing_year")} label="Manufacturing Year *" type="number" testId="sc-mfg-year" />
          <Field {...fieldProps("registration_year")} label="Registration Year" type="number" testId="sc-reg-year" />
          <Field {...fieldProps("registration_number")} label="Registration Number" testId="sc-reg-no" />
          <Field {...fieldProps("kilometres")} label="Kilometres *" type="number" testId="sc-km" />
          <SelectField name="fuel" label="Fuel" options={["Petrol","Diesel","Hybrid","Electric","CNG"]} value={form.fuel} onChange={(v) => setForm({ ...form, fuel: v })} testId="sc-fuel" />
          <SelectField name="transmission" label="Transmission" options={["Automatic","Manual","AMT","CVT","DCT"]} value={form.transmission} onChange={(v) => setForm({ ...form, transmission: v })} testId="sc-trans" />
          <SelectField name="ownership" label="Ownership" options={["1st","2nd","3rd","4th+"]} value={form.ownership} onChange={(v) => setForm({ ...form, ownership: v })} testId="sc-own" />
          <Field {...fieldProps("colour")} label="Colour" testId="sc-colour" />
          <Field {...fieldProps("expected_price")} label="Expected Price (₹)" type="number" testId="sc-price" />
        </Section>

        <Section title="Vehicle condition" cols={2}>
          <div className="md:col-span-2">
            <label className="overline text-[#242424]/60 block mb-2">Condition</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {CONDITIONS.map((c) => (
                <button type="button" key={c} onClick={() => setForm({ ...form, condition: c })} className={`py-3 rounded-sm text-sm border hairline transition-colors ${form.condition === c ? "bg-[#7A1519] text-white border-[#7A1519]" : "bg-white hover:border-[#7A1519]"}`} data-testid={`sc-cond-${c.toLowerCase().replace(/\s+/g,'-')}`}>{c}</button>
              ))}
            </div>
          </div>
        </Section>

        <div>
          <div className="overline text-[#242424]/60 mb-2">Photos ({images.length}/10)</div>
          <label className="border-2 border-dashed hairline block p-8 text-center cursor-pointer hover:border-[#7A1519] transition-colors rounded-sm" data-testid="sc-upload-zone">
            <UploadCloud className="mx-auto text-[#7A1519]" />
            <div className="mt-2 text-sm">{uploading ? "Uploading…" : "Click or drag images here"}</div>
            <div className="text-xs text-[#242424]/60">JPG, PNG, WEBP · up to 15MB each · max 10 photos</div>
            <input type="file" accept="image/*" multiple hidden disabled={uploading} onChange={(e) => handleFiles(e.target.files)} data-testid="sc-file-input" />
          </label>
          {images.length > 0 && (
            <div className="mt-3 grid grid-cols-3 md:grid-cols-5 gap-2">
              {images.map((im, i) => (
                <div key={i} className="relative aspect-square overflow-hidden rounded-sm border hairline">
                  <img src={resolveImageUrl(im.url)} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => setImages(images.filter((_, x) => x !== i))} className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5" aria-label="Remove"><X size={12} /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        <button type="submit" disabled={busy} className="w-full md:w-auto bg-[#7A1519] hover:bg-[#5a0f12] text-white px-8 py-4 rounded-sm text-sm font-semibold tracking-wide inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="sc-submit">
          {busy ? "Sending…" : "Request Valuation"} <ArrowRight size={14} />
        </button>
      </form>
    </div>
  );
};

const Section = ({ title, children, cols = 2 }) => (
  <div>
    <div className="overline text-[#7A1519] mb-4">{title}</div>
    <div className={`grid grid-cols-1 md:grid-cols-${cols} gap-4`}>{children}</div>
  </div>
);

const Field = ({ label, value, onChange, type = "text", testId }) => (
  <div>
    <label className="overline text-[#242424]/60 block mb-2">{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-[#F7F6F3] border hairline rounded-sm px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-[#7A1519]" data-testid={testId} />
  </div>
);

const SelectField = ({ label, value, onChange, options, testId }) => (
  <div>
    <label className="overline text-[#242424]/60 block mb-2">{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-[#F7F6F3] border hairline rounded-sm px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-[#7A1519]" data-testid={testId}>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

export default SellYourCar;
